/**
 * @fileoverview Provide the month grid and day summaries for caller-supplied calendar records. `kc-internal-frontend/src/components/Calendar/BespokeCalendar.js` owns navigation; shared calendar utilities and EventComponent own time/layout conventions and action signifiers. The view delegates event/day selection without owning event persistence or authorization.
 */

import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import EventComponent from '../components/EventComponent';
import { 
  generateMonthGrid, 
  isDateInCurrentMonth, 
  isToday, 
  calculateEventPosition,
  groupEventsByDay,
  CALENDAR_VIEWS 
} from '../utils/calendarUtils';



const MonthGridView = ({ 
  currentDate, 
  events = [],
  onEventClick,
  onEventEdit,
  onEventDelete,
  onConfirmResponsibility,
  onDateClick,
  onEventDrop,
  assignees = [],
  currentUser = null
}) => {
  // Generate the calendar grid
  const monthGrid = useMemo(() => generateMonthGrid(currentDate), [currentDate]);
  
  // Group events by day for easier rendering
  const eventsByDay = useMemo(() => {
    const startDate = monthGrid[0][0];
    const endDate = monthGrid[5][6];
    return groupEventsByDay(events, startDate, endDate);
  }, [events, monthGrid]);

  // Get events for a specific day
  const getEventsForDay =

  (date) => {
    const dateKey = date.format('YYYY-MM-DD');
    return eventsByDay[dateKey] || [];
  };

  // Handle event drag and drop
  const handleEventDrop =

  (event, dropTarget, mouseEvent) => {
    if (onEventDrop) {
      const newDate = moment(dropTarget);
      onEventDrop(event, newDate);
    }
  };

  // Handle date cell click for creating new events
  const handleDateClick =

  (date, e) => {
    e.stopPropagation();
    if (onDateClick) {
      onDateClick(date);
    }
  };

  // Render week header
  const renderWeekHeader =

  () => {
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    return (
      <div className="month-grid-header">
        {weekDays.map((day, index) => (
          <div key={index} className="week-day-header">
            {day}
          </div>
        ))}
      </div>
    );
  };



  const renderDayCell = (date, weekIndex, dayIndex) => {
    const dayEvents = getEventsForDay(date);
    const isCurrentMonth = isDateInCurrentMonth(date, currentDate);
    const isTodayDate = isToday(date);
    const cellKey = `${weekIndex}-${dayIndex}`;

    const cellClasses = [
      'month-day-cell',
      isCurrentMonth ? 'current-month' : 'other-month',
      isTodayDate ? 'today' : '',
      dayEvents.length > 0 ? 'has-events' : ''
    ].filter(Boolean).join(' ');

    return (
      <div
        key={cellKey}
        className={cellClasses}
        onClick={(e) => handleDateClick(date, e)}
        onDrop={(e) => {
          e.preventDefault();
          const eventData = JSON.parse(e.dataTransfer.getData('application/json'));
          handleEventDrop(eventData, date, e);
        }}
        onDragOver={(e) => e.preventDefault()}
        data-date={date.format('YYYY-MM-DD')}
      >
        <div className="day-number">
          {date.format('D')}
        </div>
        
        <div className="day-events">
          {dayEvents.map((event, eventIndex) => (
            <EventComponent
              key={`${event.id}-${date.format('YYYY-MM-DD')}`}
              event={event}
              viewType={CALENDAR_VIEWS.MONTH_GRID}
              isCompact={true}
              isDraggable={true}
              style={{
                position: 'relative',
                marginBottom: '1px',
                width: '100%',
                left: 0,
                top: 'auto'
              }}
              onEventClick={onEventClick}
              onEventEdit={onEventEdit}
              onEventDelete={onEventDelete}
              onConfirmResponsibility={onConfirmResponsibility}
              assignees={assignees}
              currentUser={currentUser}
            />
          ))}
        </div>
      </div>
    );
  };

  // Render week row
  const renderWeekRow =

  (week, weekIndex) => {
    return (
      <div key={weekIndex} className="month-week-row">
        {week.map((date, dayIndex) => renderDayCell(date, weekIndex, dayIndex))}
      </div>
    );
  };

  return (
    <div className="month-grid-view">
      {renderWeekHeader()}
      <div className="month-grid-body">
        {monthGrid.map((week, weekIndex) => renderWeekRow(week, weekIndex))}
      </div>
    </div>
  );
};

export default MonthGridView; 